/**
 * What every **open** union's preset lookup does with a value it does not know:
 * say it out loud once in dev, and paint it anyway. Shared by `pill/preset.ts`
 * and `severity/preset.ts` — one copy so the two resolvers cannot drift on
 * whether a surprise value is noticed, or on how it reads once it lands.
 *
 * Open unions get a fallback because **none of the backing database columns are
 * real enums** — they are `VARCHAR`/`Text` with the enum enforced only in
 * application code. The fallback keeps the cell rendering (P-17); the warning is
 * what stops the fallback from hiding a genuine contract change in silence.
 */

/** Warn once per domain+value — a table renders the same bad row every frame. */
const warned = new Set<string>()

/**
 * @param domain Named in the message — e.g. `'case status'`, `'severity'`.
 * @param value The already-normalised unknown value.
 */
export function warnUnknownValueOnce(domain: string, value: string) {
	const key = `${domain}:${value}`
	if (warned.has(key)) return
	warned.add(key)
	console.warn(
		`Unknown ${domain} value "${value}" — falling back to neutral. ` +
			`Add it to the preset record, or confirm the backend can actually emit it.`,
	)
}

/**
 * Test-only: the warn-once `Set` is module-level and leaks across tests.
 *
 * @internal — the tag is load-bearing, not decorative: `check:dead` runs knip
 * in production mode, where a test importer confers nothing.
 */
export function resetUnknownValueWarnings() {
	warned.clear()
}

/**
 * `not_started` → `Not started`. Only ever applied to values a preset record
 * does not know, so it never overrides an authored label.
 *
 * **Sentence** case, not Title Case, because that is how the authored labels
 * next to it read (`Not started`, `In progress`) — a fallback that shouted
 * `Not Started` would be legible as "the frontend has never heard of this",
 * which is the dev warning's job, not the operator's problem.
 *
 * Deliberately *not* `shared/parsing`'s `toDisplayName`: that one title-cases
 * and expands acronyms for field names and vendor keys (`source_ip` →
 * `Source IP`). Status values are prose, and it does not split on `-`.
 */
export function humaniseUnknownValue(value: string): string {
	const words = value.replace(/[_-]+/g, ' ').trim()
	return words.charAt(0).toUpperCase() + words.slice(1)
}
